/**
 * LeftFacePanel — face profiles grid + upload button.
 */

import { useState } from "react";
import { PanelSection } from "../shell/Panel";
import type { FaceProfile } from "@/components/tabs/FaceSwapTab";
import { API_BASE } from "@/lib/api";

export function LeftFacePanel({ profiles, uploading, uploadMsg, onUpload, onActivate }: {
  profiles: FaceProfile[];
  uploading: boolean;
  uploadMsg: string | null;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onActivate: (profileId: string) => void;
}) {
  return (
    <div style={{ height: "100%", overflowY: "auto" }}>
      <PanelSection
        label="Faces"
        action={
          <label style={{
            fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)",
            color: "var(--accent)", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px"
          }}>
            <span style={{ fontSize: "var(--text-2xs)", color: "var(--text-muted)", fontWeight: "normal" }}>Max 15 MB</span>
            + Add
            <input type="file" accept="image/*" onChange={onUpload}
              style={{ display: "none" }} disabled={uploading} />
          </label>
        }
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-2)" }}>
          {profiles.map(p => (
            <FaceTile key={p.profile_id} profile={p} onActivate={onActivate} />
          ))}
          {profiles.length === 0 && (
            <div style={{
              gridColumn: "1 / -1", padding: "var(--space-6)",
              textAlign: "center", color: "var(--text-muted)",
              fontSize: "var(--text-sm)", border: "1px dashed var(--border-subtle)",
              borderRadius: "var(--radius-lg)",
            }}>
              Upload a face photo to start
            </div>
          )}
        </div>
        {uploadMsg && (
          <div style={{
            marginTop: "var(--space-2)", padding: "var(--space-2) var(--space-3)",
            borderRadius: "var(--radius-md)", fontSize: "var(--text-xs)",
            background: uploadMsg.startsWith("✓") ? "var(--status-live-dim)" : "var(--status-error-dim)",
            color: uploadMsg.startsWith("✓") ? "var(--status-live)" : "var(--status-error)",
            border: `1px solid ${uploadMsg.startsWith("✓") ? "var(--status-live)" : "var(--status-error)"}`,
          }}>
            {uploadMsg}
          </div>
        )}
      </PanelSection>
    </div>
  );
}

function FaceTile({ profile, onActivate }: { profile: FaceProfile; onActivate: (profileId: string) => void }) {
  const imgSrc = profile.thumbnail_url || (profile as any).dataUrl || (profile.profile_id ? `${API_BASE()}/api/face/profiles/${profile.profile_id}/thumbnail` : "");
  const [loadFailed, setLoadFailed] = useState(false);

  return (
    <div
      onClick={() => onActivate(profile.profile_id)}
      style={{
        position: "relative",
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
        border: `2px solid ${profile.is_active ? "var(--accent)" : "var(--border-subtle)"}`,
        background: "var(--bg-elevated)",
        cursor: "pointer",
        transition: "border-color var(--duration-fast)",
        aspectRatio: "1",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {imgSrc && !loadFailed ? (
        <img
          src={imgSrc}
          alt={profile.name}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          onError={() => setLoadFailed(true)}
        />
      ) : (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          background: "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(5,150,105,0.04))",
          color: "var(--text-secondary)",
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span style={{ fontSize: "10px", marginTop: "4px", color: "var(--text-muted)", fontWeight: "var(--weight-semibold)" }}>
            {profile.name ? profile.name.slice(0, 4) : "Face"}
          </span>
        </div>
      )}
      {profile.is_active && (
        <div style={{
          position: "absolute", top: 4, right: 4,
          width: 8, height: 8, borderRadius: "var(--radius-full)",
          background: "var(--accent)",
          boxShadow: "0 0 0 2px var(--bg-overlay)",
          zIndex: 2,
        }} />
      )}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        padding: "var(--space-1) var(--space-2)",
        background: "linear-gradient(transparent, rgba(0,0,0,0.8))",
        fontSize: "var(--text-xs)", color: "#fff",
        fontWeight: "var(--weight-medium)",
        zIndex: 2,
      }} className="truncate">
        {profile.name}
      </div>
    </div>
  );
}
